import store from '../../../../store/store';
import { K12_OUT_OF_STOCK_ERROR_CODE, NATIONAL_STORE, PRODUCT_AVAILABLE } from '../../../../common/utils/cart-util';
import { ShopCart } from '../interfaces/shop-cart.interface';
import { StoreState } from '../../../../store/store.interface';
import { getFreeTrialDuration } from '../../../free-trial/utils/free-trial.util';
import { getImageSrcUrl, isProfessional, mapFormatDescription, mapDurationFromExtensionAttributes, mapEditionOrRelease } from '../../../../common/utils/product-util';
import { isAdminUser, isSegUser, selectUser } from '../../../user-profile/user-profile.selectors';
import { isMagentoCartItemsShippable } from '../store/shop-cart.selectors';
import { isTunnelPurchaseFlow, parse, stripNotApplicableValues } from '../../../../common/utils/util';
import { mapCreateCoverImages, mapCreateTermsToDuration } from '../../../create-custom/mappings/create-custom.mapping';
import { sanitizeInstructors } from '../../../create-custom/utils/create-custom-product.util';
import { mapMetaOfferContents } from './meta-offer.mapping';

export const mapMagentoCartResponseToShopCart = (magentoCartResponse, totals?) => {
  const state = store.getState() as StoreState;
  const shopResponse: ShopCart = {
    products: magentoCartResponse.items.map((product) => {
      const extensionAttributes = product.extension_attributes;
      const hasGrade = !!extensionAttributes.min_grade;
      const mappedDuration = mapDurationFromExtensionAttributes(extensionAttributes);
      const isProfessionalProduct = isProfessional(extensionAttributes?.segment ?? '');

      return {
        item_id: product.item_id,
        productIdentifier: {
          sku: product.sku,
          offerPricingId: extensionAttributes?.product_offer_id,
          mhid: extensionAttributes?.mhid,
          isbn10: extensionAttributes?.isbn,
          isbn13: extensionAttributes?.isbn13,
          businessUnit: extensionAttributes?.segment === "K12" ? "SEG": extensionAttributes?.segment,
          programSourceId: extensionAttributes.program_source_id ? extensionAttributes.program_source_id[0]: "",
          programSourceName: extensionAttributes.program_source_name,
          looseLeafAvailable: extensionAttributes?.loose_leaf_available,
          hasEbook: extensionAttributes?.is_ebook,
          isConnectSharpenBundle: extensionAttributes?.is_sharpen,
          isSharpenCompanion: extensionAttributes?.is_sharpen_companion,
          isSharpenAdvantage: extensionAttributes?.is_sharpen_advantage,
          ...(mappedDuration.term !== undefined ? { term: mappedDuration.term } : {}),
        },
        quantity: product.qty,
        priceInfo: {
          netPrice:
            +extensionAttributes.net_price != product.price
              ? +extensionAttributes.net_price
              : product.price,
          customPrice: product.price,
          currencyCode:
          extensionAttributes?.currency_code || magentoCartResponse.currency?.quote_currency_code,
          countryCode: extensionAttributes?.country_code,
          listPrice: +extensionAttributes.list_price,
        },
        availabilityInfo: {
          available: isProductAvailable(product),
          errorCode: extensionAttributes?.error_code,
          errorMessage: extensionAttributes?.error_message,
        },
        createdDate: extensionAttributes?.created_at,
        modifiedDate: extensionAttributes?.updated_at,
        // heebook: false,
        productMetadata: {
          title: product.name,
          contributors: extensionAttributes.author_info?.map((author: any) => stripNotApplicableValues(author.display_name || (`${author.given_name}${author.family_name ? " " + author.family_name : ""}`))),
          ...(mappedDuration.duration !== undefined ? { duration: mappedDuration.duration } : {}),
          ...(mappedDuration.durationPeriod !== undefined ? { durationPeriod: mappedDuration.durationPeriod } : {}),
          edition: extensionAttributes.edition,
          editionOrRelease: mapEditionOrRelease(extensionAttributes),
          publicationDate: extensionAttributes.publication_date,
          copyrightYear: extensionAttributes.copyright,
          imageUrl: getImageSrcUrl(extensionAttributes?.isbn13 || extensionAttributes?.isbn),
          virtual: extensionAttributes?.digital === '1' || extensionAttributes?.is_digital,
          grades: hasGrade ? extensionAttributes?.min_grade + ' - ' + extensionAttributes?.max_grade : '',
          application: extensionAttributes?.application,
          format: mapFormatDescription(extensionAttributes),
          isEvergreen: extensionAttributes.is_evergreen,
          offerType: extensionAttributes.offer_type,
          offerBranding: extensionAttributes.offer_branding,
          itemType: extensionAttributes?.item_type,
          flow: extensionAttributes?.flow,
          formatTypeDesc: extensionAttributes?.format_type_desc,
          durationType: extensionAttributes?.base_product ? 'standard' : 'alternate',
          ...mapCreateTermsToDuration(extensionAttributes?.terms),
          metaOfferContentsMetadata: mapMetaOfferContents(extensionAttributes?.meta_offer_contents),
          ...(isProfessionalProduct && extensionAttributes.title_type_code !== undefined && { titleType: extensionAttributes.title_type_code }),
          ...(isProfessionalProduct && extensionAttributes.division !== undefined && { division: extensionAttributes.division }),
        },
        createCustomProductMetadata: {
          courses: parse(extensionAttributes?.course_info) || [],
          createCoverImages: mapCreateCoverImages(extensionAttributes?.cover_images),
          deliveryTypes: parse(extensionAttributes?.delivery_type) || [],
          department: extensionAttributes?.department,
          instructorNames: sanitizeInstructors(parse(extensionAttributes?.instructor_name) || []),
          school: parse(extensionAttributes?.school),
          supplements: parse(extensionAttributes?.supplements) || [],
        },
        //Be very carefull and align within the analytics team if you need to change any property under this object
        freeTrialInfo: {
          isFreeTrial: extensionAttributes?.free_trial,
          startDate: extensionAttributes?.trial_start_date,
          freeTrialDuration: extensionAttributes?.trial_end_date ? getFreeTrialDuration(extensionAttributes.trial_start_date, extensionAttributes.trial_end_date) : '0',
          endDate: extensionAttributes?.trial_end_date,
          subscriptionAmount: extensionAttributes?.subscription_amount,
          subscriptionUnitPrice: extensionAttributes?.subscription_unit_price,
          subscriptionTax: extensionAttributes?.subscription_tax,
          subscriptionDigitalServiceFees: extensionAttributes?.subscription_digital_service_fees,
        },
        cancelUrl: extensionAttributes?.cancel_url,
        exitUrl: extensionAttributes?.exit_url,
        ...(product.quote_id && {quoteId: product.quote_id}),
      };
    }),
    // sampleCart: boolean;
    productCount: magentoCartResponse.items_qty,
    showPoOption: !isTunnelPurchaseFlow() && isSegUser(selectUser(state)) && isAdminUser(state),
    isPurchaseOrder: magentoCartResponse.extension_attributes?.is_purchase_order,
    poNumber: magentoCartResponse.extension_attributes?.purchase_order_number,
    cartId: magentoCartResponse.id,
    discount: totals?.discount_amount ? Math.abs(totals.discount_amount) : 0,
    shippable: isMagentoCartItemsShippable(magentoCartResponse.items),
    subTotal: magentoCartResponse.items.reduce((acc, product) => acc + extractItemPriceForSubtotal(product) * product.qty, 0),
    bundleDiscount: magentoCartResponse.items.reduce((acc, product) => acc + extractBundleDiscountFromProduct(product) * product.qty, 0),
    metaOfferId: magentoCartResponse.extension_attributes?.meta_offer_id,
    totalAmount:
      totals?.base_subtotal_with_discount ??
      magentoCartResponse.items.reduce((acc, product) => acc + product.price * product.qty, 0),
    orderBusinessUnit: magentoCartResponse.extension_attributes?.order_business_unit,
    userBusinessUnit: magentoCartResponse.extension_attributes?.user_business_unit,
    userRole: magentoCartResponse.extension_attributes?.user_role,
    erpAccountNumber: magentoCartResponse.extension_attributes?.erp_customer_id,
    store: getStoreName(magentoCartResponse),
    storeName: getStoreName(magentoCartResponse),
    itemsCount: magentoCartResponse.items_count,
    storeType: magentoCartResponse.extension_attributes?.store_type || magentoCartResponse.store_type,
    currencyCode: totals?.base_currency_code,
    intlTaxAmount: totals?.tax_amount,
    isTunnelPurchase: isTunnelPurchaseFlow(),
    availability: magentoCartResponse.extension_attributes?.availability,
    guestCheckoutEmail: magentoCartResponse.items[0]?.extension_attributes?.email,
    errorMessage: magentoCartResponse.extension_attributes?.error_message
  };

  return shopResponse;
};

function extractItemPriceForSubtotal(product: any) {
  if (isDiscountedBundlePrice(product)) {
    return product.extension_attributes.list_price;
  }

  return product.price;
}

function extractBundleDiscountFromProduct(product: any) {
  if (isDiscountedBundlePrice(product)) {
    return product.extension_attributes.list_price - product.price;
  }

  return 0;
}

/**
  If magento didn't return it later on directly on the API, we need to update it for SEG Admin Users as they pay the netPrice
  Then, in that scenario the list will be always different than the customPrice here.

  We are checking if the offerPricingId exists, as it will limit it to edoms B2C scope of just HE products.
 */
function isDiscountedBundlePrice(product) {
  return !isTunnelPurchaseFlow() && (product.extension_attributes.list_price !== product.price) && product.extension_attributes.product_offer_id;
}

function getStoreName(magentoCartResponse) {
  return magentoCartResponse.extension_attributes?.custom_store_code ||
    _getCustomStoreCodeFromCartItems(magentoCartResponse.items) ||
    NATIONAL_STORE
}

function _getCustomStoreCodeFromCartItems(items) {
  if (items.length > 0) {
    return items[0].extension_attributes.custom_store_code;
  }
  return '';
}

export const isProductOutOfStock = (product) => product.extension_attributes?.error_code === K12_OUT_OF_STOCK_ERROR_CODE;

const isProductAvailable = (product) =>
  isProductOutOfStock(product) || product.extension_attributes?.availability == PRODUCT_AVAILABLE;
